/**
 * palette.ts — colour mapping for flow kinds + overlay annotations.
 *
 * Two palettes live here:
 *  - FLOW_KIND_COLORS: the base colour for each edge's `flowKind`. Drives the
 *    "every system of interactions coloured differently" spec requirement.
 *    Custom kinds hash their label to a stable colour so scenarios can name
 *    their own flow types (e.g. "ingredients", "meals") without code changes.
 *  - OVERLAY_COLORS: the Phase 4 annotation colours (bottleneck, anomaly,
 *    cluster, decision). These reuse the handbook convention (AGENTS.md):
 *    red = bad, green = good, blue = neutral — so signals read as signals,
 *    not decoration.
 */
import type { FlowKind, LegendEntry } from '../engine/types';

export type { LegendEntry };

export const FLOW_KIND_COLORS: Record<FlowKind, string> = {
  goods: '#55a868', // green — physical stuff moving
  money: '#dd9f4b', // gold — money
  customers: '#8e6ca8', // purple — people
  orders: '#4c72b0', // blue — requests
  signal: '#9aa0aa', // grey — informational
  custom: '#9aa0aa', // overridden by hashKindColor
};

/** Stable colour for a custom flow kind, keyed on its label. Uses a tiny
 *  string-hash into a fixed palette so "ingredients" and "meals" get distinct
 *  but consistent colours across reloads. */
const CUSTOM_PALETTE = [
  '#c44e52',
  '#8e6ca8',
  '#dd9f4b',
  '#76b7b2',
  '#edaf3c',
  '#4878d0',
  '#ee6677',
  '#6acc64',
];

/** The shared swatch set for user-picked colours (edge/node custom colours in
 *  the graph + workflow editors — one palette everywhere). */
export { CUSTOM_PALETTE };

export function flowKindColor(kind: FlowKind, customKind?: string, customColor?: string): string {
  if (kind !== 'custom' || !customKind) return FLOW_KIND_COLORS[kind];
  // An explicit colour from the picker wins over the label-hash.
  if (customColor) return customColor;
  let h = 0;
  for (let i = 0; i < customKind.length; i++) h = (h * 31 + customKind.charCodeAt(i)) | 0;
  return CUSTOM_PALETTE[Math.abs(h) % CUSTOM_PALETTE.length];
}

// ─── Overlay colours (Phase 4 annotations) ──────────────────────────────────
export const OVERLAY_COLORS = {
  bottleneck: '#c44e52', // red — the bad one
  anomaly: '#edaf3c', // amber — caution
  decision: '#55a868', // green — the agent acted
  clusterRing: '#4c72b0', // blue — neutral grouping
} as const;

// ─── Group colours ──────────────────────────────────────────────────────────
/** The single source of truth for node-group colours (the canvas rects, the
 *  sidebar dots, and the NodeModal pills all resolve through `groupColor`).
 *  Stable + scenario-agnostic — hashed off the group id so any project's
 *  groups get a deterministic palette across reloads. */
export const GROUP_PALETTE = [
  '#4c72b0',
  '#55a868',
  '#dd9f4b',
  '#8e6ca8',
  '#76b7b2',
  '#c44e52',
  '#ee6677',
  '#6acc64',
];

const groupColorCache = new Map<string, string>();

/** Stable colour for a group id. Same hash trick as flowKindColor so group
 * labels like "supply" or "production" are deterministic. */
export function groupColor(id: string): string {
  const cached = groupColorCache.get(id);
  if (cached) return cached;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const c = GROUP_PALETTE[Math.abs(h) % GROUP_PALETTE.length];
  groupColorCache.set(id, c);
  return c;
}

/** Muted gray for agent-less activity (the model's own annotations). */
export const MODEL_ACTIVITY_COLOR = '#9aa0aa';

const agentColorCache = new Map<string, string>();

/** Stable colour for an agent identity key (the timeline's decision marks
 * and the activity modal's badges share it, so a colour MEANS one agent in
 * both places). Same hash trick as groupColor; `null` (the model itself)
 * gets the muted gray. */
export function agentColor(id: string | null): string {
  if (id === null) return MODEL_ACTIVITY_COLOR;
  const cached = agentColorCache.get(id);
  if (cached) return cached;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const c = GROUP_PALETTE[Math.abs(h) % GROUP_PALETTE.length];
  agentColorCache.set(id, c);
  return c;
}

/** Legend entries the sidebar renders so the colours are legible to a viewer
 *  who hasn't seen the graph before. Built from the colours actually in use.
 *  This is the default derived when a bundle has no saved `legend`; once the
 *  user edits the legend, the persisted list wins. */
export function flowLegend(customKinds: Set<string>): LegendEntry[] {
  const entries: LegendEntry[] = [
    { label: 'Goods', color: FLOW_KIND_COLORS.goods },
    { label: 'Money', color: FLOW_KIND_COLORS.money },
    { label: 'Orders', color: FLOW_KIND_COLORS.orders },
    { label: 'Customers', color: FLOW_KIND_COLORS.customers },
    { label: 'Signal', color: FLOW_KIND_COLORS.signal },
  ];
  for (const k of customKinds) {
    entries.push({ label: k, color: flowKindColor('custom', k) });
  }
  return entries;
}
